import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
} from "../../../common/MuiImports";
import { promoCodeService } from "../../../../services/apiServices";

interface Product {
  plan_name: string;
  product_code: string;
  speed?: string | undefined | null;
  speed_unit?: string | undefined | null;
  original_price?: string | undefined | null;
  discount?: {
    discounted_price?: string | undefined | null;
    amount_off?: string | undefined | null;
    discount_duration?: string | undefined | null;
    promo_code?: string | undefined | null;
  };
  additional_devices?: Device[] | undefined | null;
  selected_duration?: string;
  isAetcDeal?: boolean;
}

interface Device {
  device_name?: string;
  device_detail_name?: string;
  device_price?: string;
  product_code?: string;
}

interface TableSummaryProps {
  data: any;
  selectedProduct: Product;
  isCheckoutPage?: boolean;
  onTotalPriceChange?: (price: number) => void;
  onValidPromoCode?: (code: string) => void;
}

const TableSummary: React.FC<TableSummaryProps> = ({
  data,
  selectedProduct,
  isCheckoutPage = false,
  onTotalPriceChange,
  onValidPromoCode,
}) => {
  const [promoCode, setPromoCode] = useState("");
  const [isPromoApplied, setIsPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [apiDiscountAmount, setApiDiscountAmount] = useState<number | null>(
    null
  );
  const [apiFinalPrice, setApiFinalPrice] = useState<number | null>(null);
  const [isResetApplied, setIsResetApplied] = useState(false);

  // Set initial promo code from selectedProduct
  useEffect(() => {
    if (selectedProduct.discount?.promo_code) {
      setPromoCode(selectedProduct.discount.promo_code);
      setIsPromoApplied(true);
      onValidPromoCode?.(selectedProduct.discount.promo_code);
    }
  }, [selectedProduct]);

  const isMonthlyPlan =
    !selectedProduct.selected_duration ||
    selectedProduct.selected_duration === "monthly";

  const isFreePlan =
    selectedProduct.plan_name.toLowerCase() === "free" ||
    selectedProduct.isAetcDeal;

  const hasDiscount =
    selectedProduct.discount && selectedProduct.discount.amount_off;
  const originalPrice = parseFloat(selectedProduct?.original_price || "0");
  const discountAmount = hasDiscount
    ? parseFloat(selectedProduct.discount?.amount_off || "0")
    : 0;

  // Handle promo code change
  const handlePromoCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPromoCode = event.target.value;
    setPromoCode(newPromoCode);
    setIsPromoApplied(false);
    setPromoError("");
    setApiDiscountAmount(null);
    setApiFinalPrice(null);
  };

  // Handle reset promo code
  const handleResetPromoCode = () => {
    setPromoCode("");
    setIsPromoApplied(false);
    setPromoError("");
    setApiDiscountAmount(null);
    setApiFinalPrice(null);
    setIsResetApplied(true);
    onValidPromoCode?.("");
  };

  // Handle apply promo code
  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }

    setIsLoading(true);
    setPromoError("");
    setIsResetApplied(false);

    try {
      const response = await promoCodeService({
        discount_code: promoCode,
        product_code: selectedProduct.product_code,
        venue_id: data?.configs?.gloop_id,
      });

      setIsPromoApplied(true);
      if (response.data?.discount_amount) {
        setApiDiscountAmount(parseFloat(response.data.discount_amount));
      } else {
        setApiDiscountAmount(0);
      }
      if (response.data?.final_price) {
        setApiFinalPrice(parseFloat(response.data.final_price));
      }
      onValidPromoCode?.(promoCode);
    } catch (error: any) {
      setIsPromoApplied(false);
      setApiDiscountAmount(null);
      setApiFinalPrice(null);
      onValidPromoCode?.("");
      setPromoError(error.message || "Invalid promo code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate effective discount amount and price
  const effectiveDiscountAmount = apiDiscountAmount ?? discountAmount;
  const effectiveDiscountedPrice =
    apiFinalPrice ??
    originalPrice - (isPromoApplied ? effectiveDiscountAmount : 0);

  const additionalDevicePrice = selectedProduct.additional_devices?.[0]
    ? parseFloat(selectedProduct.additional_devices[0].device_price || "0")
    : 0;

  const modemPrice = data?.modem
    ? parseFloat(data.modem.modem_price || "0")
    : 0;

  const totalPrice =
    (isMonthlyPlan ? effectiveDiscountedPrice : originalPrice) +
    additionalDevicePrice +
    modemPrice;

  useEffect(() => {
    onTotalPriceChange?.(totalPrice);
  }, [totalPrice, onTotalPriceChange]);

  // Show discount if there's a valid discount (either from API or product default)
  const shouldShowDiscount =
    isMonthlyPlan &&
    !isResetApplied &&
    ((isPromoApplied &&
      (apiDiscountAmount !== null || effectiveDiscountAmount > 0)) ||
      (hasDiscount && !promoError));

  if (selectedProduct.isAetcDeal) {
    return null; // Return null to hide the entire component if isAetcDeal is true
  }

  return (
    <>
      <Paper
        elevation={0}
        data-test-id="order-summary-container"
        sx={{
          padding: "20px",
          marginBottom: "20px",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="body2"
          gutterBottom
          data-test-id="order-summary-title"
          sx={{ fontFamily: "Figtree-Bold", fontSize: "16px" }}
        >
          Order Summary
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
          data-test-id="plan-details"
        >
          <Typography data-test-id="plan-name">
            {selectedProduct.plan_name} - up to {selectedProduct.speed}{" "}
            {selectedProduct.speed_unit}
          </Typography>
          <Typography data-test-id="plan-price">
            ${originalPrice.toFixed(2)}
          </Typography>
        </Box>
        {shouldShowDiscount && !isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
            data-test-id="discount-details"
          >
            <Typography>Discount</Typography>
            <Typography data-test-id="discount-amount" color="error">
              -${effectiveDiscountAmount.toFixed(2)}
            </Typography>
          </Box>
        )}

        {selectedProduct.additional_devices?.[0] && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
            data-test-id="additional-device-details"
          >
            <Typography data-test-id="device-name">
              {selectedProduct.additional_devices[0].device_detail_name}
            </Typography>
            <Typography data-test-id="device-price">
              ${additionalDevicePrice.toFixed(2)}
            </Typography>
          </Box>
        )}

        {data?.modem && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
            data-test-id="modem-details"
          >
            <Typography data-test-id="modem-name">
              {data.modem.modem_nice_name}
            </Typography>
            <Typography data-test-id="modem-price">
              ${parseFloat(data.modem.modem_price).toFixed(2)}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "bold",
            borderTop: "1px solid #e0e0e0",
            paddingTop: 2,
            marginTop: 2,
          }}
          data-test-id="total-price-container"
        >
          <Typography sx={{ fontFamily: "Figtree-Bold" }}>
            Total Price
          </Typography>
          <Typography
            data-test-id="total-price"
            sx={{ fontFamily: "Figtree-Bold" }}
          >
            ${totalPrice.toFixed(2)}
          </Typography>
        </Box>
      </Paper>

      {isCheckoutPage && isMonthlyPlan && !isFreePlan && (
        <Box className="promo-code-container" data-test-id="promo-code-section">
          <Box sx={{ display: "flex", width: "100%" }}>
            <TextField
              value={promoCode}
              onChange={handlePromoCodeChange}
              label="Enter Discount Code"
              variant="outlined"
              error={!!promoError}
              helperText={promoError}
              data-test-id="promo-code-input"
              sx={{
                marginRight: "10px",
                flexGrow: 1,
                backgroundColor: "#fff",
              }}
            />
            <Button
              disableElevation
              variant="contained"
              onClick={
                isPromoApplied ? handleResetPromoCode : handleApplyPromoCode
              }
              data-test-id="apply-promo-button"
              sx={{
                height: "55px",
                backgroundColor: isLoading ? "#ccc" : "#111",
                color: "white",
                "&:hover": {
                  backgroundColor: isLoading ? "#ccc" : "#9a0007",
                },
              }}
            >
              {isLoading ? "Applying..." : isPromoApplied ? "Reset" : "Apply"}
            </Button>
          </Box>
          {isPromoApplied && !promoError && (
            <Typography
              sx={{
                color: "#0288d1",
                marginTop: "10px",
                fontSize: "13px",
                backgroundColor: "#e3f2fd",
                padding: "8px 16px",
                borderRadius: "4px",
                border: "1px solid #81d4fa",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "-6px",
                  left: "20px",
                  width: "10px",
                  height: "10px",
                  backgroundColor: "#e3f2fd",
                  border: "1px solid #81d4fa",
                  transform: "rotate(45deg)",
                  borderBottom: "none",
                  borderRight: "none",
                },
              }}
            >
              Nice! Enjoy your discount
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};

export default TableSummary;
